import { valibotResolver } from "@hookform/resolvers/valibot";
import { Link as LinkIcon } from "@phosphor-icons/react";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { Button } from "common/components/ui/Button";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTrigger,
} from "common/components/ui/Dialog";
import {
  Drawer,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader,
  DrawerTrigger,
} from "common/components/ui/Drawer";
import FormField from "common/components/ui/FormField";
import { Input } from "common/components/ui/Input";
import useTwBreakpoint from "common/hooks/useTwBreakpoint";

import { NavigatorUrlSchema } from "../../../schemas";

interface UrlTargetingProps {
  createSource: (url: string) => void;
  isCreatingSource: boolean;
}

export default function UrlTargeting({
  createSource,
  isCreatingSource,
}: UrlTargetingProps) {
  const isDesktop = useTwBreakpoint("lg");
  const [isOpen, setIsOpen] = useState(false);

  const {
    register,
    formState: { errors },
    handleSubmit,
    reset,
  } = useForm({
    defaultValues: {
      url: "",
    },
    resolver: valibotResolver(NavigatorUrlSchema),
  });

  function onSubmit({ url }: { url: string }) {
    createSource(url);
  }

  const Component = isDesktop ? Dialog : Drawer;
  const Content = isDesktop ? DialogContent : DrawerContent;
  const Trigger = isDesktop ? DialogTrigger : DrawerTrigger;
  const Footer = isDesktop ? DialogFooter : DrawerFooter;
  const Body = isDesktop ? DialogBody : DrawerBody;
  const Header = isDesktop ? DialogHeader : DrawerHeader;

  return (
    <Component open={isOpen} onOpenChange={setIsOpen} onAfterClose={reset}>
      <Trigger asChild>
        <Button
          size="sm"
          leftIcon={<LinkIcon />}
          variant="outline-black"
          className="lg:ml-auto"
        >
          Search by URL
        </Button>
      </Trigger>
      <Content>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Header title="Paste Sales Navigator Search URL" />
          <Body>
            <FormField
              size="md"
              label="Sales Navigator Search URL"
              htmlFor="navigator-search-url"
              hint="Correct format starts with: 'https://www.linkedin.com/sales/search/people?query=...'"
              error={errors.url?.message}
            >
              <Input
                id="navigator-search-url"
                variant="md"
                placeholder="Enter URL here"
                {...register("url")}
              />
            </FormField>
          </Body>
          <Footer className="justify-end">
            <Button
              variant="secondary-black"
              onClick={() => setIsOpen(false)}
              disabled={isCreatingSource}
            >
              Cancel
            </Button>

            <Button type="submit" isLoading={isCreatingSource}>
              Apply
            </Button>
          </Footer>
        </form>
      </Content>
    </Component>
  );
}
